import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PROOF_REJECTION_PRESETS } from "@/lib/proofTypes";

interface ProofRejectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void;
  busy?: boolean;
}

export function ProofRejectionDialog({
  open,
  onOpenChange,
  onConfirm,
  busy = false,
}: ProofRejectionDialogProps) {
  const [preset, setPreset] = useState<string>(PROOF_REJECTION_PRESETS[0]);
  const [customReason, setCustomReason] = useState("");

  const buildReason = () => {
    if (preset === "Other (see custom message)") {
      return customReason.trim() || "Proof not satisfactory — please revise and resubmit.";
    }
    if (customReason.trim()) {
      return `${preset}\n\n${customReason.trim()}`;
    }
    return preset;
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Why is this proof not satisfactory?</AlertDialogTitle>
          <AlertDialogDescription>
            The hunter will see this feedback and can revise their proof (within the revision limit).
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-2">
            <Label>Select a reason</Label>
            <select
              className="w-full px-3 py-2 border-2 border-[hsl(var(--paper-edge))] rounded-lg bg-[hsl(var(--paper))] text-foreground"
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
            >
              {PROOF_REJECTION_PRESETS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="custom-rejection">Additional details (optional)</Label>
            <Textarea
              id="custom-rejection"
              rows={3}
              maxLength={500}
              placeholder="Explain what the hunter should fix..."
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
            />
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            className="bg-red-600 hover:bg-red-700"
            onClick={(e) => {
              e.preventDefault();
              onConfirm(buildReason());
            }}
          >
            Send rejection
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
